import type { InputHTMLAttributes } from 'react'
import { IconCheck } from '../../../shared/console'
import { PasswordInput } from '../../../shared/components'
import { IconCheckCircle, IconCircle, IconLock } from './icons'
import { FIELD_WITH_ICON } from './styles'

/**
 * The pieces of Figma P4 "Set your password" (11:551): the three-step
 * progress row, a password field with a lock, and the live rule checklist.
 */

export interface StepDef {
  title: string
  caption: string
  state: 'done' | 'current' | 'next'
}

export function Stepper({ steps }: { steps: readonly StepDef[] }) {
  return (
    <ol aria-label="Progress" className="flex flex-wrap items-center gap-x-5 gap-y-3">
      {steps.map((s, i) => (
        <li key={s.title} aria-current={s.state === 'current' ? 'step' : undefined} className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-medium ${
              s.state === 'done'
                ? 'bg-leaf text-white'
                : s.state === 'current'
                  ? 'bg-primary text-primary-ink'
                  : 'border border-line-strong bg-surface text-ink-soft'
            }`}
          >
            {s.state === 'done' ? <IconCheck size={14} strokeWidth={2.5} /> : i + 1}
          </span>
          <span className="flex flex-col">
            <span className={`text-[13px] leading-4 ${s.state === 'next' ? 'text-ink-soft' : 'font-medium text-ink'}`}>
              {s.title}
              <span className="sr-only">
                {s.state === 'done' ? ' (done)' : s.state === 'current' ? ' (current step)' : ' (next)'}
              </span>
            </span>
            <span className="text-[12px] leading-4 text-muted">{s.caption}</span>
          </span>
        </li>
      ))}
    </ol>
  )
}

/** PasswordInput (show/hide toggle included) with the console lock icon. */
export function LockedPasswordInput(props: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'>) {
  return (
    <div className="relative">
      <IconLock
        size={16}
        className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-ink-soft"
      />
      <PasswordInput {...props} className={FIELD_WITH_ICON} />
    </div>
  )
}

export function PasswordChecklist({
  id,
  items,
}: {
  id: string
  items: readonly { label: string; met: boolean }[]
}) {
  return (
    <ul id={id} className="mt-3 flex flex-col gap-1.5" aria-live="polite">
      {items.map((it) => (
        <li
          key={it.label}
          className={`flex items-center gap-2 text-[12px] leading-4 ${it.met ? 'text-success' : 'text-muted'}`}
        >
          {it.met ? <IconCheckCircle size={15} /> : <IconCircle size={15} />}
          <span>
            {it.label}
            <span className="sr-only">{it.met ? ' — done' : ' — not yet'}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}
